/**
 * <TypingDots> — trois points qui s'allument l'un après l'autre (atom).
 *
 * Le signe qu'une réponse s'écrit : il tient dans une bulle, à la place du texte qui arrive. Ce
 * n'est pas une roue de chargement — rien ne tourne, aucun contenu n'attend derrière : c'est la
 * suite d'un message que l'élève vient d'envoyer, et les mots qui le disent (« Sam réfléchit… »)
 * sont posés à côté par `<TypingIndicator>`.
 *
 * Motion du livre : un point à la fois, en `theme.motion.entree`, sans rebond ni déplacement —
 * seule l'opacité bouge. En mouvement réduit, les trois points sont fixes.
 *
 * Géométrie intrinsèque en nombres nus (le diamètre d'un point) ; l'écart et la couleur viennent
 * des jetons.
 */
import React, { useEffect, useMemo, useRef } from "react"
import { Animated, Easing, Platform, View } from "react-native"
import { StyleSheet, useUnistyles } from "react-native-unistyles"

import { useReduceMotion } from "../../hooks/useReduceMotion"
import { type SemanticColorKey } from "../../theme/tokens/colors"

/** Le diamètre d'un point. */
const DOT_SIZE = 8
const DOTS = [0, 1, 2] as const
/** L'opacité d'un point au repos, entre deux allumages. */
const REST_OPACITY = 0.35
/**
 * Celle des trois points en mouvement réduit : fixes, ils ne s'allument jamais. Le double du repos,
 * pour qu'ils se lisent sans le point allumé qui les fait remarquer.
 */
const STILL_OPACITY = REST_OPACITY * 2

export type TypingDotsProps = {
    /** La couleur des points — `text-muted` par défaut. */
    color?: SemanticColorKey
    testID?: string
}

export function TypingDots(props: TypingDotsProps) {
    const { color = "text-muted", testID } = props

    const reduceMotion = useReduceMotion()
    const { motion } = useUnistyles().theme
    const progress = useRef(new Animated.Value(0)).current
    // Une fois par montage : recréées à chaque rendu, le pilote natif détacherait puis rattacherait ses nœuds.
    const dotOpacities = useMemo(
        () =>
            DOTS.map((index) =>
                progress.interpolate({
                    inputRange: [index, index + 0.5, index + 1],
                    outputRange: [REST_OPACITY, 1, REST_OPACITY],
                    extrapolate: "clamp",
                })
            ),
        [progress]
    )

    useEffect(() => {
        if (reduceMotion) {
            progress.setValue(0)
            return
        }
        // Un tour = trois temps, un par point : « une chose à la fois ».
        const loop = Animated.loop(
            Animated.timing(progress, {
                toValue: DOTS.length,
                duration: motion.entree * DOTS.length,
                easing: Easing.linear,
                // react-native-web n'a pas de pilote natif ; y prétendre ne fait qu'avertir.
                useNativeDriver: Platform.OS !== "web",
            })
        )
        loop.start()
        return () => loop.stop()
    }, [reduceMotion, progress, motion])

    return (
        <View style={styles.row} testID={testID} accessibilityElementsHidden importantForAccessibility="no">
            {DOTS.map((index) => (
                <Animated.View
                    key={index}
                    style={[styles.dot(color), { opacity: reduceMotion ? STILL_OPACITY : dotOpacities[index] }]}
                />
            ))}
        </View>
    )
}

const styles = StyleSheet.create((theme) => ({
    row: {
        flexDirection: "row",
        alignItems: "center",
        gap: theme.spacing["1"],
    },
    dot: (color: SemanticColorKey) => ({
        width: DOT_SIZE,
        height: DOT_SIZE,
        borderRadius: DOT_SIZE / 2,
        backgroundColor: theme.colors[color],
    }),
}))
